import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Copy, Pencil, Plus, RotateCcw, Star, Trash2 } from "lucide-react";
import { Skeleton } from "../ui/Skeleton";
import { cn } from "../../lib/cn";
import type {
  CustomFoodView,
  FavoriteView,
  LibraryEntry,
  RecipeView,
  SavedMealView,
} from "../../screens/library-utils";
import { entryKcal, kcalLabel, recipePerServing } from "../../screens/library-utils";

// Library section chrome + row components (plan todo 19). Presentational
// only — all behavior arrives via callbacks so renderToStaticMarkup suites
// and the demo screen can drive them with spies/static data.

// ── Section chrome ──────────────────────────────────────────────────────────

export interface LibrarySectionProps {
  readonly testId: string;
  readonly title: string;
  readonly count?: number;
  readonly children: ReactNode;
}

export function LibrarySection({ testId, title, count, children }: LibrarySectionProps) {
  return (
    <details open data-testid={testId} className="rounded-2xl border border-border bg-surface">
      <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between px-4 py-3 select-none">
        <span className="text-sm font-semibold text-text">{title}</span>
        {count !== undefined && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-text-muted">
            {count}
          </span>
        )}
      </summary>
      <div className="flex flex-col gap-2 border-t border-border px-3 py-3">{children}</div>
    </details>
  );
}

export interface SectionBodyProps {
  readonly sectionKey: string;
  readonly data: readonly unknown[] | undefined;
  readonly searching: boolean;
  readonly search: string;
  readonly emptyState: ReactNode;
  readonly renderRows: () => ReactNode;
}

/** Loading / empty / no-matches / rows state machine for every section. */
export function SectionBody({ sectionKey, data, searching, search, emptyState, renderRows }: SectionBodyProps) {
  if (data === undefined) {
    return (
      <div data-testid={`library-section-loading-${sectionKey}`} className="flex flex-col gap-2">
        <Skeleton className="h-14 rounded-xl" />
        <Skeleton className="h-14 rounded-xl" />
      </div>
    );
  }
  if (data.length === 0) {
    if (searching) {
      return (
        <p data-testid={`library-no-matches-${sectionKey}`} className="px-1 py-2 text-sm text-text-muted">
          No matches for “{search}”.
        </p>
      );
    }
    return <>{emptyState}</>;
  }
  return <>{renderRows()}</>;
}

// ── Shared buttons ──────────────────────────────────────────────────────────

const iconButtonClass = cn(
  "flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full",
  "text-text-muted active:bg-muted active:scale-[0.98] select-none",
);

export interface IconActionProps {
  readonly testId: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly onClick: () => void;
  readonly danger?: boolean;
}

export function IconAction({ testId, label, icon: Icon, onClick, danger }: IconActionProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={label}
      className={cn(iconButtonClass, danger && "text-danger")}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Icon size={18} />
    </button>
  );
}

export interface StarActionProps {
  readonly testId: string;
  readonly name: string;
  readonly favorite: boolean;
  readonly onToggle: () => void;
}

export function StarAction({ testId, name, favorite, onToggle }: StarActionProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      aria-label={favorite ? `Unfavourite ${name}` : `Favourite ${name}`}
      aria-pressed={favorite}
      className={cn(iconButtonClass, favorite && "text-warning")}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      <Star size={18} fill={favorite ? "currentColor" : "none"} />
    </button>
  );
}

const rowActionClass = cn(
  "inline-flex min-h-[44px] items-center gap-1.5 rounded-xl px-3 text-xs font-medium",
  "text-text-muted active:bg-muted active:scale-[0.98] select-none",
);

export interface TextActionProps {
  readonly testId: string;
  readonly label: string;
  readonly icon: LucideIcon;
  readonly onClick: () => void;
  readonly danger?: boolean;
}

export function TextAction({ testId, label, icon: Icon, onClick, danger }: TextActionProps) {
  return (
    <button
      type="button"
      data-testid={testId}
      className={cn(rowActionClass, danger && "text-danger")}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

// ── Entry rows (recent / frequent foods) ────────────────────────────────────

export interface EntryRowProps {
  readonly entry: LibraryEntry;
  readonly count?: number;
  readonly onLogAgain: (entry: LibraryEntry) => void;
  readonly onAddToPlate: (entry: LibraryEntry) => void;
}

export function EntryRow({ entry, count, onLogAgain, onAddToPlate }: EntryRowProps) {
  return (
    <div
      data-testid={`library-entry-${entry._id}`}
      className="flex min-h-[44px] items-center justify-between gap-2 rounded-xl border border-border bg-surface px-3 py-2"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text">{entry.name}</p>
        <p className="text-xs text-text-muted">
          {entry.quantity !== undefined && entry.unit !== undefined
            ? `${entry.quantity} ${entry.unit} · `
            : ""}
          {kcalLabel(entryKcal(entry))}
          {count !== undefined ? ` · logged ${count}×` : ""}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <IconAction
          testId={`entry-log-again-${entry._id}`}
          label={`Log ${entry.name} again`}
          icon={RotateCcw}
          onClick={() => onLogAgain(entry)}
        />
        <IconAction
          testId={`entry-add-plate-${entry._id}`}
          label={`Add ${entry.name} to plate`}
          icon={Plus}
          onClick={() => onAddToPlate(entry)}
        />
      </div>
    </div>
  );
}

// ── Saved meal rows ─────────────────────────────────────────────────────────

export interface SavedMealRowProps {
  readonly meal: SavedMealView;
  readonly onLogAgain: (meal: SavedMealView) => void;
  readonly onAddToPlate: (meal: SavedMealView) => void;
  readonly onToggleFavorite: (meal: SavedMealView) => void;
  readonly onEdit: (meal: SavedMealView) => void;
  readonly onDelete: (meal: SavedMealView) => void;
}

export function SavedMealRow({ meal, onLogAgain, onAddToPlate, onToggleFavorite, onEdit, onDelete }: SavedMealRowProps) {
  return (
    <div
      data-testid={`saved-meal-row-${meal._id}`}
      className="flex flex-col gap-1 rounded-xl border border-border bg-surface px-3 py-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text">{meal.name}</p>
          <p className="text-xs text-text-muted">
            {meal.mealType !== undefined ? (
              <span className="capitalize">{meal.mealType}</span>
            ) : (
              "Any meal"
            )}
            {" · "}
            {kcalLabel(meal.totalCalories)}
            {meal.timesUsed > 0 ? ` · used ${meal.timesUsed}×` : ""}
          </p>
        </div>
        <StarAction
          testId={`saved-meal-fav-${meal._id}`}
          name={meal.name}
          favorite={meal.favorite}
          onToggle={() => onToggleFavorite(meal)}
        />
      </div>
      <div className="flex flex-wrap gap-1">
        <TextAction
          testId={`saved-meal-log-again-${meal._id}`}
          label="Log again"
          icon={RotateCcw}
          onClick={() => onLogAgain(meal)}
        />
        <TextAction
          testId={`saved-meal-add-plate-${meal._id}`}
          label="Add to plate"
          icon={Plus}
          onClick={() => onAddToPlate(meal)}
        />
        <TextAction
          testId={`saved-meal-edit-${meal._id}`}
          label="Edit"
          icon={Pencil}
          onClick={() => onEdit(meal)}
        />
        <TextAction
          testId={`saved-meal-delete-${meal._id}`}
          label="Delete"
          icon={Trash2}
          danger
          onClick={() => onDelete(meal)}
        />
      </div>
    </div>
  );
}

// ── Custom food rows ────────────────────────────────────────────────────────

export interface CustomFoodRowProps {
  readonly food: CustomFoodView;
  readonly onLogAgain: (food: CustomFoodView) => void;
  readonly onAddToPlate: (food: CustomFoodView) => void;
  readonly onToggleFavorite: (food: CustomFoodView) => void;
  readonly onEdit: (food: CustomFoodView) => void;
  readonly onDuplicate: (food: CustomFoodView) => void;
  readonly onDelete: (food: CustomFoodView) => void;
}

export function CustomFoodRow({ food, onLogAgain, onAddToPlate, onToggleFavorite, onEdit, onDuplicate, onDelete }: CustomFoodRowProps) {
  return (
    <div
      data-testid={`custom-food-row-${food._id}`}
      className="flex flex-col gap-1 rounded-xl border border-border bg-surface px-3 py-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text">{food.name}</p>
          <p className="text-xs text-text-muted">
            {food.brand !== undefined ? `${food.brand} · ` : ""}
            {food.servingUnit} · {kcalLabel(food.calories)}
          </p>
        </div>
        <StarAction
          testId={`custom-food-fav-${food._id}`}
          name={food.name}
          favorite={food.favorite}
          onToggle={() => onToggleFavorite(food)}
        />
      </div>
      <div className="flex flex-wrap gap-1">
        <TextAction
          testId={`custom-food-log-again-${food._id}`}
          label="Log again"
          icon={RotateCcw}
          onClick={() => onLogAgain(food)}
        />
        <TextAction
          testId={`custom-food-add-plate-${food._id}`}
          label="Add to plate"
          icon={Plus}
          onClick={() => onAddToPlate(food)}
        />
        <TextAction
          testId={`custom-food-edit-${food._id}`}
          label="Edit"
          icon={Pencil}
          onClick={() => onEdit(food)}
        />
        <TextAction
          testId={`custom-food-duplicate-${food._id}`}
          label="Duplicate"
          icon={Copy}
          onClick={() => onDuplicate(food)}
        />
        <TextAction
          testId={`custom-food-delete-${food._id}`}
          label="Delete"
          icon={Trash2}
          danger
          onClick={() => onDelete(food)}
        />
      </div>
    </div>
  );
}

// ── Recipe rows ─────────────────────────────────────────────────────────────

export interface RecipeRowProps {
  readonly recipe: RecipeView;
  readonly onLogAgain: (recipe: RecipeView) => void;
  readonly onAddToPlate: (recipe: RecipeView) => void;
  readonly onToggleFavorite: (recipe: RecipeView) => void;
  readonly onEdit: (recipe: RecipeView) => void;
  readonly onDuplicate: (recipe: RecipeView) => void;
  readonly onDelete: (recipe: RecipeView) => void;
}

export function RecipeRow({ recipe, onLogAgain, onAddToPlate, onToggleFavorite, onEdit, onDuplicate, onDelete }: RecipeRowProps) {
  const perServing = recipePerServing(recipe);
  return (
    <div
      data-testid={`recipe-row-${recipe._id}`}
      className="flex flex-col gap-1 rounded-xl border border-border bg-surface px-3 py-3"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-text">{recipe.name}</p>
          <p className="text-xs text-text-muted">
            {recipe.servings !== undefined ? `${recipe.servings} servings · ` : ""}
            {kcalLabel(perServing?.calories)} per serving
            {recipe.totals?.complete === false ? " · incomplete" : ""}
          </p>
        </div>
        <StarAction
          testId={`recipe-fav-${recipe._id}`}
          name={recipe.name}
          favorite={recipe.favorite}
          onToggle={() => onToggleFavorite(recipe)}
        />
      </div>
      <div className="flex flex-wrap gap-1">
        <TextAction
          testId={`recipe-log-again-${recipe._id}`}
          label="Log again"
          icon={RotateCcw}
          onClick={() => onLogAgain(recipe)}
        />
        <TextAction
          testId={`recipe-add-plate-${recipe._id}`}
          label="Add to plate"
          icon={Plus}
          onClick={() => onAddToPlate(recipe)}
        />
        <TextAction
          testId={`recipe-edit-${recipe._id}`}
          label="Edit"
          icon={Pencil}
          onClick={() => onEdit(recipe)}
        />
        <TextAction
          testId={`recipe-duplicate-${recipe._id}`}
          label="Duplicate"
          icon={Copy}
          onClick={() => onDuplicate(recipe)}
        />
        <TextAction
          testId={`recipe-delete-${recipe._id}`}
          label="Delete"
          icon={Trash2}
          danger
          onClick={() => onDelete(recipe)}
        />
      </div>
    </div>
  );
}

// ── Favourite rows ──────────────────────────────────────────────────────────

const FAVORITE_KIND_LABELS: Record<FavoriteView["kind"], string> = {
  savedMeal: "Saved meal",
  customFood: "Custom food",
  recipe: "Recipe",
  foodMemory: "Memory",
};

export interface FavoriteRowProps {
  readonly favorite: FavoriteView;
  readonly onLogAgain: (favorite: FavoriteView) => void;
  readonly onAddToPlate: (favorite: FavoriteView) => void;
  readonly onToggleFavorite: (favorite: FavoriteView) => void;
}

export function FavoriteRow({ favorite, onLogAgain, onAddToPlate, onToggleFavorite }: FavoriteRowProps) {
  return (
    <div
      data-testid={`favorite-row-${favorite.kind}-${favorite.refId}`}
      className="flex min-h-[44px] items-center justify-between gap-2 rounded-xl border border-border bg-surface px-3 py-2"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-text">{favorite.name}</p>
        <p className="text-xs text-text-muted">
          {FAVORITE_KIND_LABELS[favorite.kind]}
          {favorite.servingLabel !== undefined ? ` · ${favorite.servingLabel}` : ""}
          {" · "}
          {kcalLabel(favorite.calories)}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <IconAction
          testId={`favorite-log-again-${favorite.kind}-${favorite.refId}`}
          label={`Log ${favorite.name} again`}
          icon={RotateCcw}
          onClick={() => onLogAgain(favorite)}
        />
        <IconAction
          testId={`favorite-add-plate-${favorite.kind}-${favorite.refId}`}
          label={`Add ${favorite.name} to plate`}
          icon={Plus}
          onClick={() => onAddToPlate(favorite)}
        />
        <StarAction
          testId={`favorite-unfav-${favorite.kind}-${favorite.refId}`}
          name={favorite.name}
          favorite
          onToggle={() => onToggleFavorite(favorite)}
        />
      </div>
    </div>
  );
}
